import { COLORS } from "../../shared/constants";
import { useState, useEffect } from "react";
import * as api from "../../api";

export default function CalendarTab() {
  const [status, setStatus] = useState({ available: false, connected: false, credentials_configured: false, client_id: "" });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Calendar selection (Step 3)
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState("");
  const [savingCalendar, setSavingCalendar] = useState(false);

  useEffect(() => {
    api.calendarStatus().then((s) => {
      setStatus(s);
      if (s.selected_calendar?.id) setSelectedCalendarId(s.selected_calendar.id);
      // Auto-load calendar list if already connected
      if (s.connected) fetchCalendars();
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchCalendars = async () => {
    setLoadingCalendars(true);
    try {
      const res = await api.calendarListCalendars();
      setCalendars(res.calendars || []);
    } catch (e) {
      // Non-critical — show inline error only if user is on Step 3
    } finally {
      setLoadingCalendars(false);
    }
  };

  const handleConnect = async () => {
    setMessage(null);
    try {
      const res = await api.calendarGetAuthUrl();
      const authWindow = window.open(res.auth_url, "google-auth", "width=500,height=600");
      const poll = setInterval(async () => {
        try {
          const s = await api.calendarStatus();
          if (s.connected) {
            clearInterval(poll);
            setStatus(s);
            if (s.selected_calendar?.id) setSelectedCalendarId(s.selected_calendar.id);
            setMessage({ type: "success", text: "Google Calendar connected! Choose which calendar to use below." });
            if (authWindow && !authWindow.closed) authWindow.close();
            fetchCalendars();
          }
        } catch (e) {}
      }, 2000);
      setTimeout(() => clearInterval(poll), 120000);
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Failed to start authorization." });
    }
  };

  const handleDisconnect = async () => {
    setMessage(null);
    try {
      await api.calendarDisconnect();
      setStatus(prev => ({ ...prev, connected: false, selected_calendar: null }));
      setCalendars([]);
      setSelectedCalendarId("");
      setMessage({ type: "success", text: "Google Calendar disconnected." });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Failed to disconnect." });
    }
  };

  const handleSaveCalendarSelection = async () => {
    if (!selectedCalendarId) return;
    setSavingCalendar(true); setMessage(null);
    try {
      const cal = calendars.find(c => c.id === selectedCalendarId);
      await api.calendarSaveSelectedCalendar({ id: selectedCalendarId, summary: cal?.summary || "" });
      setStatus(prev => ({ ...prev, selected_calendar: { id: selectedCalendarId, summary: cal?.summary || "" } }));
      setMessage({ type: "success", text: `Now using "${cal?.summary || selectedCalendarId}" for Today's Schedule and scheduling.` });
    } catch (e) {
      setMessage({ type: "error", text: e.error || "Failed to save calendar selection." });
    }
    setSavingCalendar(false);
  };

  if (loading) {
    return <div style={{ padding: "20px", color: COLORS.textSecondary }}>Checking calendar status...</div>;
  }

  const inputStyle = {
    width: "100%", height: "40px", padding: "0 12px", borderRadius: "8px",
    border: `1px solid ${COLORS.border}`, fontSize: "13px", color: COLORS.text,
    background: COLORS.surface, boxSizing: "border-box",
  };

  const savedCalendarId = status.selected_calendar?.id;
  const calendarSelectionSaved = savedCalendarId && savedCalendarId === selectedCalendarId;

  return (
    <div>
      <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "6px" }}>
        Google Calendar
      </h3>
      <p style={{ fontSize: "13px", color: COLORS.textSecondary, marginBottom: "20px", lineHeight: "1.5" }}>
        Connect to Google Calendar to view today's schedule and create appointments from the EHR.
      </p>

      {message && (
        <div style={{
          padding: "10px 14px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px",
          background: message.type === "success" ? "#E8F8F0" : COLORS.dangerLight,
          color: message.type === "success" ? "#2E9E6A" : COLORS.danger,
          border: `1px solid ${message.type === "success" ? "#A7E8C5" : COLORS.dangerBorder}`,
        }}>
          {message.text}
        </div>
      )}

      {!status.available ? (
        <div style={{ padding: "16px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "8px", fontSize: "13px", color: "#92400E" }}>
          Google API libraries not installed. Run on your server:<br />
          <code style={{ background: "#FEF3C7", padding: "2px 6px", borderRadius: "4px", fontSize: "12px" }}>
            pip3 install google-auth google-auth-oauthlib google-api-python-client
          </code>
        </div>
      ) : (
        <>
          {/* Step 1: Credentials */}
          <div style={{ padding: "16px", background: COLORS.bg, border: `1px solid ${COLORS.borderLight}`, borderRadius: "8px", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <span style={{
                width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "12px", fontWeight: "700", color: "#fff",
                background: status.credentials_configured ? "#2ECC71" : COLORS.primary,
              }}>
                {status.credentials_configured ? "✓" : "1"}
              </span>
              <span style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text }}>
                Google API Credentials
              </span>
              {status.credentials_configured && (
                <span style={{ fontSize: "11px", color: "#2ECC71", fontWeight: "500" }}>Configured</span>
              )}
            </div>

            {!status.credentials_configured ? (
              <div style={{ fontSize: "12px", color: COLORS.textSecondary, lineHeight: "1.5" }}>
                <p style={{ marginBottom: "8px" }}>
                  Google API credentials are not configured. Set these environment variables on the server
                  (in <code style={{ background: COLORS.inputBg, padding: "1px 4px", borderRadius: "3px", fontSize: "11px" }}>/opt/tms-ehr/tms-ehr.env</code>):
                </p>
                <code style={{ display: "block", background: COLORS.inputBg, padding: "8px 12px", borderRadius: "6px", fontSize: "11px", lineHeight: "1.8" }}>
                  GOOGLE_CLIENT_ID=your-id.apps.googleusercontent.com<br />
                  GOOGLE_CLIENT_SECRET=GOCSPX-your-secret
                </code>
                <p style={{ marginTop: "8px", fontSize: "11px" }}>
                  Then restart the service: <code style={{ background: COLORS.inputBg, padding: "1px 4px", borderRadius: "3px", fontSize: "11px" }}>sudo systemctl restart tms-ehr</code>
                </p>
              </div>
            ) : (
              <div style={{ fontSize: "12px", color: COLORS.textSecondary }}>
                Client ID: <code style={{ fontSize: "11px", color: COLORS.text }}>{status.client_id?.substring(0, 30)}...</code>
              </div>
            )}
          </div>

          {/* Step 2: Connect */}
          <div style={{ padding: "16px", background: COLORS.bg, border: `1px solid ${COLORS.borderLight}`, borderRadius: "8px", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <span style={{
                width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "12px", fontWeight: "700", color: "#fff",
                background: status.connected ? "#2ECC71" : status.credentials_configured ? COLORS.primary : COLORS.border,
              }}>
                {status.connected ? "✓" : "2"}
              </span>
              <span style={{ fontSize: "14px", fontWeight: "600", color: status.credentials_configured ? COLORS.text : COLORS.textSecondary }}>
                Connect Calendar
              </span>
            </div>

            {!status.connected ? (
              <div>
                <p style={{ fontSize: "12px", color: COLORS.textSecondary, marginBottom: "14px", lineHeight: "1.5" }}>
                  {status.credentials_configured
                    ? "Click below to authorize access to your Google Calendar. A Google sign-in window will open."
                    : "Complete Step 1 first to configure your Google API credentials."}
                </p>
                <button onClick={handleConnect} disabled={!status.credentials_configured}
                  style={{
                    height: "40px", padding: "0 20px", borderRadius: "8px", border: "none",
                    background: status.credentials_configured ? "#4285F4" : COLORS.border,
                    color: "#fff", fontSize: "13px", fontWeight: "600",
                    cursor: status.credentials_configured ? "pointer" : "default",
                    opacity: status.credentials_configured ? 1 : 0.5,
                    display: "flex", alignItems: "center", gap: "8px",
                  }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Connect Google Calendar
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: "500", color: "#166534" }}>
                    ✓ Connected to Google Calendar
                  </div>
                  <div style={{ fontSize: "12px", color: "#166534", marginTop: "2px" }}>
                    Choose which calendar to use in Step 3 below.
                  </div>
                </div>
                <button onClick={handleDisconnect}
                  style={{
                    height: "34px", padding: "0 14px", borderRadius: "6px",
                    border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight,
                    color: COLORS.danger, fontSize: "12px", fontWeight: "600", cursor: "pointer",
                  }}>
                  Disconnect
                </button>
              </div>
            )}
          </div>

          {/* Step 3: Choose Calendar (only shown when connected) */}
          {status.connected && (
            <div style={{ padding: "16px", background: COLORS.bg, border: `1px solid ${COLORS.borderLight}`, borderRadius: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <span style={{
                  width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "12px", fontWeight: "700", color: "#fff",
                  background: calendarSelectionSaved ? "#2ECC71" : COLORS.primary,
                }}>
                  {calendarSelectionSaved ? "✓" : "3"}
                </span>
                <span style={{ fontSize: "14px", fontWeight: "600", color: COLORS.text }}>
                  Choose Calendar
                </span>
                {calendarSelectionSaved && (
                  <span style={{ fontSize: "11px", color: "#2ECC71", fontWeight: "500" }}>Saved</span>
                )}
              </div>

              <p style={{ fontSize: "12px", color: COLORS.textSecondary, marginBottom: "12px", lineHeight: "1.5" }}>
                Select which calendar to use for Today's Schedule and new appointments. Choose your Simple Practice–linked calendar here.
              </p>

              {loadingCalendars ? (
                <div style={{ fontSize: "13px", color: COLORS.textSecondary }}>Loading calendars...</div>
              ) : calendars.length === 0 ? (
                <div style={{ fontSize: "13px", color: COLORS.textSecondary }}>
                  No calendars found.{" "}
                  <button onClick={fetchCalendars} style={{ fontSize: "13px", color: COLORS.primary, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                    Retry
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "11px", fontWeight: "600", color: COLORS.textSecondary, display: "block", marginBottom: "4px" }}>
                      Calendar
                    </label>
                    <div style={{ position: "relative" }}>
                      {/* Color swatch for selected calendar */}
                      {selectedCalendarId && (() => {
                        const cal = calendars.find(c => c.id === selectedCalendarId);
                        return cal?.background_color ? (
                          <span style={{
                            position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)",
                            width: "10px", height: "10px", borderRadius: "50%",
                            background: cal.background_color, display: "inline-block",
                          }} />
                        ) : null;
                      })()}
                      <select
                        value={selectedCalendarId}
                        onChange={(e) => setSelectedCalendarId(e.target.value)}
                        style={{
                          ...inputStyle,
                          paddingLeft: selectedCalendarId && calendars.find(c => c.id === selectedCalendarId)?.background_color ? "28px" : "12px",
                          appearance: "none", cursor: "pointer",
                        }}
                      >
                        <option value="">— Select a calendar —</option>
                        {calendars.map(cal => (
                          <option key={cal.id} value={cal.id}>
                            {cal.summary}{cal.primary ? " (Primary)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={handleSaveCalendarSelection}
                    disabled={!selectedCalendarId || savingCalendar}
                    style={{
                      height: "40px", padding: "0 20px", borderRadius: "8px", border: "none",
                      background: !selectedCalendarId ? COLORS.border : COLORS.primary,
                      color: "#fff", fontSize: "13px", fontWeight: "600",
                      cursor: !selectedCalendarId || savingCalendar ? "default" : "pointer",
                      opacity: !selectedCalendarId ? 0.5 : 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {savingCalendar ? "Saving..." : "Save"}
                  </button>
                </div>
              )}

              {savedCalendarId && (
                <div style={{ marginTop: "10px", fontSize: "12px", color: COLORS.textSecondary }}>
                  Currently using: <strong style={{ color: COLORS.text }}>{status.selected_calendar?.summary || savedCalendarId}</strong>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
